import { marked } from 'marked';

import { splitMarkdown } from './frontmatter';

export type BlogPostMeta = {
  title: string;
  description: string;
  slug: string;
  publishedAt: string;
  updatedAt: string;
  tags: string[];
  draft: boolean;
};

export type BlogPost = BlogPostMeta & {
  html: string;
  readingTimeMinutes: number;
};

const WORDS_PER_MINUTE = 200;

const rawModules = import.meta.glob('../../content/blog/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

marked.setOptions({
  gfm: true,
  breaks: false,
});

const stripHtml = (html: string) =>
  html
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const getReadingTimeMinutes = (html: string): number => {
  const words = stripHtml(html).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / WORDS_PER_MINUTE));
};

const toDateString = (value: unknown, fallback: string): string => {
  if (typeof value === 'string') return value.slice(0, 10);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return fallback;
};

const parseFrontmatter = (data: Record<string, unknown>, slugFromFile: string): BlogPostMeta => {
  const title = typeof data.title === 'string' ? data.title.trim() : '';
  const description = typeof data.description === 'string' ? data.description.trim() : '';
  const slug = typeof data.slug === 'string' && data.slug.trim() ? data.slug.trim() : slugFromFile;
  const defaultDate = new Date().toISOString().slice(0, 10);
  const publishedAt = toDateString(data.publishedAt, defaultDate);
  const updatedAt = toDateString(data.updatedAt, publishedAt);
  const tags = Array.isArray(data.tags)
    ? data.tags.filter((t): t is string => typeof t === 'string')
    : [];
  const draft = data.draft === true;

  if (!title || !description) {
    throw new Error(`Blog post "${slug}" is missing required frontmatter (title, description).`);
  }

  return { title, description, slug, publishedAt, updatedAt, tags, draft };
};

const slugFromPath = (path: string): string => {
  const match = path.match(/\/([^/]+)\.md$/);
  return match?.[1] ?? path;
};

const parseAllPosts = (): BlogPost[] =>
  Object.entries(rawModules).map(([path, raw]) => {
    const { data, content } = splitMarkdown(raw);
    const fallbackSlug = slugFromPath(path);
    const meta = parseFrontmatter(data as Record<string, unknown>, fallbackSlug);
    const html = marked.parse(content) as string;

    return {
      ...meta,
      html,
      readingTimeMinutes: getReadingTimeMinutes(html),
    };
  });

export const getAllPosts = (options?: { includeDrafts?: boolean }): BlogPost[] => {
  const includeDrafts = options?.includeDrafts ?? false;

  return parseAllPosts()
    .filter((post) => includeDrafts || !post.draft)
    .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
};

export const getPostBySlug = (slug: string): BlogPost | null =>
  getAllPosts().find((post) => post.slug === slug) ?? null;

export const formatPostDate = (isoDate: string): string => {
  const date = new Date(`${isoDate}T00:00:00Z`);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
};
